/**
 * 文档管理的数据请求（TanStack Query），接口与参数与旧版页面一致
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { documentsApi, type CreateDocumentForm, type UpdateDocumentForm } from '@/api/services/documents'

const DOCUMENTS_KEY = ['admin', 'documents'] as const

export interface DocumentFilters {
  language?: string
  category?: string
  search?: string
}

export function useDocumentList(page: number, pageSize: number, filters: DocumentFilters) {
  return useQuery({
    queryKey: [...DOCUMENTS_KEY, 'list', page, pageSize, filters],
    queryFn: async () => {
      const response = await documentsApi.getDocumentList({
        page,
        page_size: pageSize,
        language: filters.language || undefined,
        category: filters.category || undefined,
        search: filters.search || undefined
      })
      return { documents: response.data ?? [], total: response.pagination?.total ?? 0 }
    },
    placeholderData: keepPreviousData
  })
}

export function useSaveDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    // 带 id 为更新，否则为新建
    mutationFn: (input: { id: number; data: UpdateDocumentForm } | { data: CreateDocumentForm }) =>
      'id' in input ? documentsApi.updateDocument(input.id, input.data) : documentsApi.createDocument(input.data),
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEY })
  })
}

export function useTogglePublish() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, isPublished }: { id: number; isPublished: boolean }) =>
      documentsApi.togglePublish(id, isPublished),
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEY })
  })
}

export function useDeleteDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => documentsApi.deleteDocument(id),
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEY })
  })
}
